import React, { useEffect, Children } from 'react';
import { motion } from 'framer-motion';
import { Clock, Users, Zap, CheckCircle2, XCircle, ArrowRight, MessageSquare, Workflow, Sparkles } from 'lucide-react';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
export function HospitalityPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <main className="relative z-10 bg-brand-black min-h-screen selection:bg-brand-orange/30" data-id="element-1086">
      {/* 1. HERO SECTION */}
      <section className="relative pt-40 pb-32 md:pt-52 md:pb-40 overflow-hidden" data-id="element-1087">
        {/* Subtle abstract background */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-orange/5 rounded-full blur-[120px] pointer-events-none" data-id="element-1088" />

        <div className="max-w-5xl mx-auto px-6 relative z-10 text-center" data-id="element-1089">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: [0.16, 1, 0.3, 1]
        }} className="text-5xl md:text-7xl font-display mb-8 tracking-tight leading-[0.95] text-balance" data-id="element-1090">
            High-Scale Hospitality Automation <br className="hidden md:block" data-id="element-1091" />
            <span className="text-brand-orange" data-id="element-1092">That Actually Works.</span>
          </motion.h1>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.1,
          ease: [0.16, 1, 0.3, 1]
        }} className="max-w-3xl mx-auto space-y-6 mb-12" data-id="element-1093">
            <p className="text-lg md:text-xl text-gray-400 leading-relaxed font-light max-w-2xl mx-auto" data-id="element-1094">
              Stop losing bookings to slow response times and burying your best
              staff in repetitive admin. We deploy autonomous AI agents that
              handle guest inquiries and operational workflows so your team can
              focus on the guest experience.
            </p>
            <p className="text-lg md:text-xl text-white font-medium" data-id="element-1095">
              If a guest has to wait for an answer, you've already lost them.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: [0.16, 1, 0.3, 1]
        }} className="flex flex-col sm:flex-row items-center justify-center gap-6" data-id="element-1096">
            <a href="mailto:sales@aijentik.com" className="w-full sm:w-auto bg-brand-orange hover:bg-brand-deep-orange text-white px-8 py-4 rounded-full font-medium transition-all duration-300 shadow-[0_0_40px_rgba(219,127,74,0.3)] hover:shadow-[0_0_60px_rgba(219,127,74,0.4)] flex items-center justify-center gap-2" data-id="element-1097">
              Get in Touch
              <ArrowRight className="w-4 h-4" data-id="element-1098" />
            </a>
            <a href="#use-cases" className="w-full sm:w-auto text-white hover:text-brand-orange px-8 py-4 font-medium transition-colors flex items-center justify-center" data-id="element-1099">
              See the Use Cases
            </a>
          </motion.div>
        </div>
      </section>

      {/* 2. INDUSTRY REALITY SECTION */}
      <section className="py-32 bg-[#050505] border-y border-white/5" data-id="element-1100">
        <div className="max-w-7xl mx-auto px-6" data-id="element-1101">
          <div className="flex flex-col lg:flex-row gap-16 lg:gap-24 items-center" data-id="element-1102">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={fadeUp} className="lg:w-1/2" data-id="element-1103">
              <h2 className="text-4xl md:text-5xl font-display leading-[1.0] mb-6 text-balance tracking-tight" data-id="element-1104">
                You're Not Just Running a Hotel.
                <br data-id="element-1105" />
                <span className="text-gray-500" data-id="element-1106">You're Managing Friction.</span>
              </h2>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={staggerContainer} className="lg:w-1/2" data-id="element-1107">
              <motion.p variants={fadeUp} className="text-xl text-gray-400 leading-relaxed mb-10 font-light" data-id="element-1108">
                Most hospitality teams aren't struggling because of service
                quality. They're losing time and revenue through operational
                friction — unanswered guest enquiries, manual admin, and
                inconsistent processes across shifts.
              </motion.p>

              <div className="space-y-6" data-id="element-1109">
                {['Guests ask a question and never get a reply', 'Your best staff spend hours on repetitive admin', 'Service quality varies depending on who is on shift'].map((point, i) => <motion.div key={i} variants={fadeUp} className="flex items-start gap-4" data-id="element-1110">
                    <div className="mt-1 bg-red-500/10 p-1 rounded-full shrink-0" data-id="element-1111">
                      <XCircle className="w-5 h-5 text-red-500/70" data-id="element-1112" />
                    </div>
                    <p className="text-lg text-gray-300" data-id="element-1113">{point}</p>
                  </motion.div>)}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 3. SOLUTION SECTION (AI AGENTS) */}
      <section id="use-cases" className="py-32 relative" data-id="element-1114">
        <div className="max-w-7xl mx-auto px-6" data-id="element-1115">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="max-w-3xl mb-20" data-id="element-1116">
            <h2 className="text-4xl md:text-5xl font-display mb-6 leading-[1.0] tracking-tight text-balance" data-id="element-1117">
              Purpose-Built Agents for Real-World Workflows.
            </h2>
            <p className="text-xl text-gray-400 font-light leading-relaxed max-w-2xl" data-id="element-1118">
              We don't sell AI. We build digital team members that understand
              your property, access your systems, and complete real tasks
              without human involvement.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid md:grid-cols-3 gap-8" data-id="element-1119">
            {[{
            icon: <Workflow className="w-6 h-6 text-brand-orange" data-id="element-1120" />,
            title: 'They understand your rules and policies',
            desc: 'Trained on your exact SOPs, cancellation policies, and property details to provide perfectly accurate answers.'
          }, {
            icon: <CheckCircle2 className="w-6 h-6 text-brand-orange" data-id="element-1121" />,
            title: 'They complete tasks, not just respond',
            desc: 'Integrated directly into your PMS to modify bookings, process upgrades, and log maintenance tickets.'
          }, {
            icon: <Clock className="w-6 h-6 text-brand-orange" data-id="element-1122" />,
            title: 'They operate 24/7 without delays',
            desc: 'Instant responses at 3 AM or during the busiest check-in rush, ensuring no guest is left waiting.'
          }].map((card, i) => <motion.div key={i} variants={fadeUp} className="bg-white/[0.02] border border-white/5 p-10 rounded-2xl hover:bg-white/[0.04] transition-all duration-700 ease-premium hover:-translate-y-2 hover:border-brand-orange/30 hover:shadow-[0_20px_40px_-15px_rgba(219,127,74,0.15)] group" data-id="element-1123">
                <div className="bg-brand-orange/10 w-12 h-12 rounded-xl flex items-center justify-center mb-8 group-hover:bg-brand-orange/20 transition-colors duration-500" data-id="element-1124">
                  {card.icon}
                </div>
                <h3 className="text-xl font-medium text-white mb-4 tracking-tight" data-id="element-1125">
                  {card.title}
                </h3>
                <p className="text-gray-400 leading-relaxed font-light" data-id="element-1126">
                  {card.desc}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* 4. TRANSFORMATION SECTION (BEFORE vs AFTER) */}
      <section className="py-32 bg-[#050505] border-y border-white/5" data-id="element-1127">
        <div className="max-w-7xl mx-auto px-6" data-id="element-1128">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid lg:grid-cols-2 gap-12 lg:gap-0 relative" data-id="element-1129">
            {/* Desktop Divider */}
            <div className="hidden lg:block absolute top-0 bottom-0 left-1/2 w-px bg-gradient-to-b from-transparent via-white/10 to-transparent -translate-x-1/2" data-id="element-1130" />

            {/* Left: The Old Way */}
            <motion.div variants={fadeUp} className="lg:pr-20" data-id="element-1131">
              <h3 className="text-sm font-bold tracking-[0.25em] text-gray-500 uppercase mb-12" data-id="element-1132">
                The Old Way
              </h3>
              <div className="space-y-10" data-id="element-1133">
                {['3pm check-in rush means the phone rings out', 'Unanswered WhatsApp enquiry leads to a lost booking', 'Missed maintenance request results in a bad review'].map((text, i) => <div key={i} className="flex gap-6 opacity-60" data-id="element-1134">
                    <XCircle className="w-6 h-6 text-gray-500 shrink-0" data-id="element-1135" />
                    <p className="text-xl text-gray-400 font-light" data-id="element-1136">{text}</p>
                  </div>)}
              </div>
            </motion.div>

            {/* Right: With Aijentik */}
            <motion.div variants={fadeUp} className="lg:pl-20" data-id="element-1137">
              <h3 className="text-sm font-bold tracking-[0.25em] text-brand-orange uppercase mb-12" data-id="element-1138">
                With Aijentik
              </h3>
              <div className="space-y-10" data-id="element-1139">
                {['Seamless flow: staff focus on guests, AI handles calls', 'Instant booking captured via autonomous chat', 'Automated resolution and ticket creation in your PMS'].map((text, i) => <div key={i} className="flex gap-6" data-id="element-1140">
                    <Sparkles className="w-6 h-6 text-brand-orange shrink-0" data-id="element-1141" />
                    <p className="text-xl text-white font-medium" data-id="element-1142">{text}</p>
                  </div>)}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 5. WHAT THIS LOOKS LIKE SECTION */}
      <section className="py-32" data-id="element-1143">
        <div className="max-w-7xl mx-auto px-6" data-id="element-1144">
          <motion.h2 initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="text-4xl md:text-5xl font-display text-center mb-20 leading-[1.0] tracking-tight text-balance" data-id="element-1145">
            What This Actually Looks Like <br className="hidden md:block" data-id="element-1146" />
            <span className="text-gray-500" data-id="element-1147">in Your Business</span>
          </motion.h2>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid md:grid-cols-3 gap-6" data-id="element-1148">
            {[{
            title: '24/7 Reservation Handling',
            desc: 'An agent that lives on your website and WhatsApp, answering complex questions about amenities, checking live availability, and driving direct bookings while you sleep.'
          }, {
            title: 'Automated Admin Workflows',
            desc: 'Pre-arrival emails, ID collection, and payment link generation handled autonomously. Your staff only step in for VIPs or complex exceptions.'
          }, {
            title: 'AI Guest Service Layer',
            desc: "In-stay requests (extra towels, late check-out, room service) are instantly routed to the correct department's system without tying up the front desk."
          }].map((card, i) => <motion.div key={i} variants={fadeUp} className="bg-[#0A0A0A] border border-white/10 p-10 rounded-2xl transition-all duration-700 ease-premium hover:-translate-y-2 hover:border-white/20 hover:shadow-2xl" data-id="element-1149">
                <h3 className="text-2xl font-display mb-4 tracking-tight" data-id="element-1150">
                  {card.title}
                </h3>
                <p className="text-gray-400 leading-relaxed font-light" data-id="element-1151">
                  {card.desc}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* 6. METHODOLOGY SECTION */}
      <section className="py-32 bg-[#050505] border-y border-white/5" data-id="element-1152">
        <div className="max-w-7xl mx-auto px-6" data-id="element-1153">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="text-center mb-20" data-id="element-1154">
            <h2 className="text-4xl md:text-5xl font-display mb-6 leading-[1.0] tracking-tight text-balance" data-id="element-1155">
              No Fluff. Just Implementation.
            </h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid md:grid-cols-3 gap-12 relative" data-id="element-1156">
            {/* Connecting line */}
            <div className="hidden md:block absolute top-8 left-[15%] right-[15%] h-px bg-white/10" data-id="element-1157" />

            {[{
            step: '01',
            title: 'Audit',
            desc: 'We map your current guest journey and identify the exact points of friction costing you time and revenue.'
          }, {
            step: '02',
            title: 'Build',
            desc: 'We train custom agents on your specific property data, tone of voice, and operational rules.'
          }, {
            step: '03',
            title: 'Integrate',
            desc: 'We connect the agents directly to your PMS, CRM, and communication channels for seamless execution.'
          }].map((item, i) => <motion.div key={i} variants={fadeUp} className="relative z-10 flex flex-col items-center text-center group" data-id="element-1158">
                <div className="w-16 h-16 rounded-full bg-brand-black border border-white/10 flex items-center justify-center text-xl font-display text-brand-orange mb-8 transition-all duration-500 group-hover:border-brand-orange/50 group-hover:bg-brand-orange/5 group-hover:shadow-[0_0_30px_rgba(219,127,74,0.2)]" data-id="element-1159">
                  {item.step}
                </div>
                <h3 className="text-2xl font-medium text-white mb-4 tracking-tight" data-id="element-1160">
                  {item.title}
                </h3>
                <p className="text-gray-400 font-light leading-relaxed max-w-sm" data-id="element-1161">
                  {item.desc}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* 7. FINAL CTA SECTION */}
      <section className="py-32 relative overflow-hidden" data-id="element-1162">
        <div className="absolute inset-0 bg-brand-orange/5" data-id="element-1163" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-brand-orange/10 rounded-full blur-[100px] pointer-events-none" data-id="element-1164" />

        <div className="max-w-4xl mx-auto px-6 relative z-10 text-center" data-id="element-1165">
          <motion.h2 initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUp} className="text-5xl md:text-6xl font-display mb-8 leading-[1.0] tracking-tight text-balance" data-id="element-1166">
            Stop Managing Friction. <br data-id="element-1167" />
            <span className="text-brand-orange" data-id="element-1168">Start Scaling Excellence.</span>
          </motion.h2>

          <motion.p initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUp} className="text-xl text-gray-300 mb-12 font-light max-w-2xl mx-auto" data-id="element-1169">
            Let's break down your operations and identify exactly where AI can
            remove bottlenecks.
          </motion.p>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUp} className="flex flex-col items-center" data-id="element-1170">
            <a href="mailto:sales@aijentik.com" className="inline-block bg-white text-brand-black hover:bg-gray-100 px-10 py-5 rounded-full font-medium text-lg transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1 mb-6" data-id="element-1171">
              Contact Us — sales@aijentik.com
            </a>
            <p className="text-sm text-gray-500 font-medium" data-id="element-1172">
              We'll get back to you within 24 hours.
            </p>
          </motion.div>
        </div>
      </section>
    </main>;
}